import React, { createContext, useContext, useState, useCallback, ReactNode, useRef } from 'react';

interface VideoPlayerContextValue {
  playingVideoId: string | null;
  registerVideo: (id: string, pauseCallback: () => void) => () => void;
  playVideo: (id: string) => void;
  clearPlayingVideo: () => void;
}

const VideoPlayerContext = createContext<VideoPlayerContextValue | undefined>(undefined);

export const VideoPlayerProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [playingVideoId, setPlayingVideoId] = useState<string | null>(null);
  const videoRefs = useRef<Map<string, () => void>>(new Map());
  const playingVideoIdRef = useRef<string | null>(null);

  // Keep ref in sync with state
  React.useEffect(() => {
    playingVideoIdRef.current = playingVideoId;
  }, [playingVideoId]);

  const registerVideo = useCallback((id: string, pauseCallback: () => void) => {
    videoRefs.current.set(id, pauseCallback);
    
    // Return unregister function
    return () => {
      videoRefs.current.delete(id);
    };
  }, []);

  const playVideo = useCallback((id: string) => {
    const currentPlayingId = playingVideoIdRef.current;
    if (currentPlayingId && currentPlayingId !== id) {
      const pauseCallback = videoRefs.current.get(currentPlayingId);
      if (pauseCallback) {
        pauseCallback();
      }
      queueMicrotask(() => setPlayingVideoId(id));
    } else {
      setPlayingVideoId(id);
    }
  }, []);

  const clearPlayingVideo = useCallback(() => {
    const currentPlayingId = playingVideoIdRef.current;
    if (currentPlayingId) {
      const pauseCallback = videoRefs.current.get(currentPlayingId);
      if (pauseCallback) {
        pauseCallback();
      }
      setPlayingVideoId(null);
    }
  }, []);

  return (
    <VideoPlayerContext.Provider
      value={{
        playingVideoId,
        registerVideo,
        playVideo,
        clearPlayingVideo,
      }}
    >
      {children}
    </VideoPlayerContext.Provider>
  );
};

export const useVideoPlayer = (): VideoPlayerContextValue => {
  const context = useContext(VideoPlayerContext);
  if (!context) {
    throw new Error('useVideoPlayer must be used within a VideoPlayerProvider');
  }
  return context;
};
